// Customer photos on a returns request. The bytes are behind the staff
// session (GET /admin/returns/photos/:id), and an <img src> can't carry the
// bearer token the staging FE relies on, so each photo is fetched with
// apiFetch and shown from a blob URL. A small module cache keeps a photo's
// URL for the session, so the lightbox and the thumbnails share one fetch.
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ImageOff, X, Download } from "lucide-react";
import { apiFetch } from "../../api/apiFetch";
import { photoPath } from "../../api/returns";
import { useTranslation } from "../../i18n";

const cache = new Map<string, Promise<string | null>>();

const loadPhoto = (photoId: string): Promise<string | null> => {
    if (!cache.has(photoId)) {
        cache.set(photoId, apiFetch(photoPath(photoId))
            .then(async (res) => (res.ok ? URL.createObjectURL(await res.blob()) : null))
            .catch(() => null));
    }
    return cache.get(photoId)!;
};

/** Browsers can't draw HEIC; those get a download link instead. */
const drawable = (mime?: string) => !mime || !/heic|heif/i.test(mime);

export const usePhotoUrl = (photoId: string | null) => {
    const [url, setUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let alive = true;
        setUrl(null);
        setFailed(false);
        if (!photoId) return;
        loadPhoto(photoId).then((u) => {
            if (!alive) return;
            if (u) setUrl(u); else setFailed(true);
        });
        return () => { alive = false; };
    }, [photoId]);
    return { url, failed };
};

interface PhotoMeta { photo_id: string; mime_type?: string }

const Thumb = ({ photo, onOpen, label }: { photo: PhotoMeta; onOpen: () => void; label: string }) => {
    const { url, failed } = usePhotoUrl(photo.photo_id);
    return (
        <button type="button" className="rt-thumb" onClick={onOpen} aria-label={label}>
            {url && drawable(photo.mime_type)
                ? <img src={url} alt="" />
                : <span className="rt-thumb-empty">{failed || !drawable(photo.mime_type) ? <ImageOff size={16} /> : null}</span>}
        </button>
    );
};

/** A row of thumbnails; clicking one opens the lightbox on it. */
export const PhotoStrip = ({ photos }: { photos: PhotoMeta[] }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<number | null>(null);
    if (photos.length === 0) return null;
    return (
        <>
            <div className="rt-thumbs">
                {photos.map((p, i) => (
                    <Thumb key={p.photo_id} photo={p} onOpen={() => setOpen(i)} label={t("rt_photo_open", { n: String(i + 1) })} />
                ))}
            </div>
            {open !== null && <Lightbox photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
        </>
    );
};

export const Lightbox = ({ photos, index, onIndex, onClose }: {
    photos: PhotoMeta[]; index: number; onIndex: (i: number) => void; onClose: () => void;
}) => {
    const { t } = useTranslation();
    const photo = photos[index];
    const { url, failed } = usePhotoUrl(photo?.photo_id ?? null);
    const prev = useCallback(() => onIndex((index - 1 + photos.length) % photos.length), [index, photos.length, onIndex]);
    const next = useCallback(() => onIndex((index + 1) % photos.length), [index, photos.length, onIndex]);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowLeft" && photos.length > 1) prev();
            else if (e.key === "ArrowRight" && photos.length > 1) next();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, prev, next, photos.length]);
    if (!photo) return null;
    return (
        <div className="rt-lightbox" role="dialog" aria-modal="true" aria-label={t("rt_photo_title")} onClick={onClose}>
            <div className="rt-lightbox-bar" onClick={(e) => e.stopPropagation()}>
                <span>{t("rt_photo_of", { n: String(index + 1), total: String(photos.length) })}</span>
                <span className="rt-lightbox-actions">
                    {url && (
                        <a className="btn btn-sm rt-btn btn-ghost rt-lightbox-dl" href={url} download={`${photo.photo_id}`}>
                            <Download size={14} /> {t("rt_photo_download")}
                        </a>
                    )}
                    <button type="button" className="rt-icon-btn rt-icon-btn--dark" onClick={onClose} aria-label={t("close")}><X size={18} /></button>
                </span>
            </div>
            <div className="rt-lightbox-stage" onClick={(e) => e.stopPropagation()}>
                {photos.length > 1 && (
                    <button type="button" className="rt-lightbox-nav rt-lightbox-nav--prev" onClick={prev} aria-label={t("rt_photo_prev")}><ChevronLeft size={22} /></button>
                )}
                {url && drawable(photo.mime_type)
                    ? <img src={url} alt={t("rt_photo_title")} />
                    : <p className="rt-lightbox-msg">{failed ? t("rt_photo_failed") : !drawable(photo.mime_type) ? t("rt_photo_heic") : t("loading")}</p>}
                {photos.length > 1 && (
                    <button type="button" className="rt-lightbox-nav rt-lightbox-nav--next" onClick={next} aria-label={t("rt_photo_next")}><ChevronRight size={22} /></button>
                )}
            </div>
        </div>
    );
};
